import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import Layout from './components/Layout';
import { ToastProvider } from './components/Toast';
import { supabase } from './lib/supabase';
import { useAuth } from './hooks/useAuth';

// --- Eager: Login (first thing user sees) ---
import Login from './pages/Login';

// --- Lazy-loaded pages (code splitting) ---
const Home = lazy(() => import('./pages/Home'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const StaffHub = lazy(() => import('./pages/StaffHub'));
const SparesSales = lazy(() => import('./pages/SparesSales'));
const Inventory = lazy(() => import('./pages/Inventory'));
const ProjectSales = lazy(() => import('./pages/ProjectSales'));
const Settings = lazy(() => import('./pages/Settings'));
const SOPs = lazy(() => import('./pages/SOPs'));
const Training = lazy(() => import('./pages/Training'));
const ViewSOP = lazy(() => import('./pages/ViewSOP'));
const UpdatePassword = lazy(() => import('./pages/UpdatePassword'));
const Holidays = lazy(() => import('./pages/Holidays'));
const Payslips = lazy(() => import('./pages/Payslips'));
const Timesheets = lazy(() => import('./pages/Timesheets'));
const MyProfile = lazy(() => import('./pages/MyProfile'));
const ProductionBoard = lazy(() => import('./pages/ProductionBoard'));
const GanttChart = lazy(() => import('./pages/GanttChart'));
const MyJobs = lazy(() => import('./pages/MyJobs'));
const Permissions = lazy(() => import('./pages/Permissions'));
const ExcelSync = lazy(() => import('./pages/ExcelSync'));

// --- Loading Fallback ---
const PageLoader = () => (
  <div className="h-[60vh] flex items-center justify-center">
    <div className="text-center">
      <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
      <p className="text-gray-400 text-sm">Loading...</p>
    </div>
  </div>
);

// --- AUTH LISTENER ---
const AuthListener = () => {
  const navigate = useNavigate();
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        navigate('/update-password');
      }
    });
    return () => subscription.unsubscribe();
  }, [navigate]);
  return null;
};

// --- PROTECTED ROUTE WRAPPER ---
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-gray-500 text-sm">Loading VCSP System...</p>
      </div>
    </div>
  );
  if (!user) return <Navigate to="/login" />;
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
};

function App() {
  return (
    <ToastProvider>
      <Router>
        <AuthListener />
        <Layout>
          <Routes>
            {/* Public */}
            <Route path="/login" element={<Login />} />
            <Route path="/update-password" element={<Suspense fallback={<PageLoader />}><UpdatePassword /></Suspense>} />

            {/* Main */}
            <Route path="/" element={<ProtectedRoute><Home /></ProtectedRoute>} />

            {/* Work Side */}
            <Route path="/worklog/sales" element={<ProtectedRoute><ProjectSales /></ProtectedRoute>} />
            <Route path="/worklog/parts" element={<ProtectedRoute><SparesSales /></ProtectedRoute>} />
            <Route path="/inventory" element={<ProtectedRoute><Inventory /></ProtectedRoute>} />

            {/* Production */}
            <Route path="/production" element={<ProtectedRoute><ProductionBoard /></ProtectedRoute>} />
            <Route path="/production/gantt" element={<ProtectedRoute><GanttChart /></ProtectedRoute>} />
            <Route path="/my-jobs" element={<ProtectedRoute><MyJobs /></ProtectedRoute>} />

            {/* Staff Hub */}
            <Route path="/staff-hub" element={<ProtectedRoute><StaffHub /></ProtectedRoute>} />
            <Route path="/holidays" element={<ProtectedRoute><Holidays /></ProtectedRoute>} />
            <Route path="/payslips" element={<ProtectedRoute><Payslips /></ProtectedRoute>} />
            <Route path="/timesheets" element={<ProtectedRoute><Timesheets /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><MyProfile /></ProtectedRoute>} />

            {/* Knowledge Base */}
            <Route path="/staff-hub/sops" element={<ProtectedRoute><SOPs /></ProtectedRoute>} />
            <Route path="/staff-hub/sops/:id" element={<ProtectedRoute><ViewSOP /></ProtectedRoute>} />
            <Route path="/staff-hub/training" element={<ProtectedRoute><Training /></ProtectedRoute>} />

            {/* Admin */}
            <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
            <Route path="/admin/permissions" element={<ProtectedRoute><Permissions /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
            <Route path="/sync" element={<ProtectedRoute><ExcelSync /></ProtectedRoute>} />

            {/* Catch-all */}
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </Layout>
      </Router>
    </ToastProvider>
  );
}

export default App;